import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Building2, ChevronRight, Loader2, Mail } from 'lucide-react'
import toast from 'react-hot-toast'

import { authApi } from '@services/api'
import type { LoginResponse } from '@services/api'
import { useAuthStore, UserCompanyRole, type User, type UserContract } from '@stores/authStore'
import { isPushSupported, isPushPermissionDenied, subscribeToPush } from '@services/pushNotifications'
import { safeNext } from '@/utils/safeRedirect'
import type { MagicLinkVerifyErrorDetail } from '@/types/auth'

type LoginUser = LoginResponse['user']
type State = 'loading' | 'company-select' | 'error'

const displayName = (user: Pick<LoginUser, 'first_name' | 'last_name' | 'email'>): string =>
  [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email

/**
 * Nachforder-Kennzeichen aus dem Verify-Fehler lesen (STORY-1038).
 * Fehlt es, ist der Token unbekannt — dann braucht es die E-Mail-Eingabe.
 */
const readRenewToken = (err: unknown): string | null => {
  if (typeof err !== 'object' || err === null) return null
  const detail = (err as { response?: { data?: { detail?: MagicLinkVerifyErrorDetail } } })
    .response?.data?.detail
  return typeof detail?.renew_token === 'string' && detail.renew_token ? detail.renew_token : null
}

/**
 * Echter Verbindungsfehler (keine Antwort vom Server). Nur der darf gemeldet
 * werden — jede Server-Antwort bleibt neutral, damit weder ein fehlendes Konto
 * noch das erreichte Nachforder-Limit erkennbar wird.
 */
const isConnectionError = (err: unknown): boolean =>
  typeof err !== 'object' || err === null || (err as { response?: unknown }).response === undefined

interface PendingAuth {
  user: LoginUser
  accessToken: string
  refreshToken: string
  companies: UserCompanyRole[]
  contracts: UserContract[]
  nextUrl: string
}

export default function MagicLinkPage() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { setAuth, setActiveCompanies, logout, isAuthenticated } = useAuthStore()
  const [state, setState] = useState<State>('loading')
  const [pending, setPending] = useState<PendingAuth | null>(null)
  // STORY-1038: Kennzeichen des abgelaufenen Tokens — erlaubt das Nachfordern
  // ohne E-Mail-Eingabe. null = Token unbekannt, Rueckfallebene mit Eingabefeld.
  const [renewToken, setRenewToken] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [sent, setSent] = useState(false)
  // STORY-1023: user that was signed in before the magic link replaced the session
  const previousUserRef = useRef<User | null>(null)

  /** Welcome toast — names the user switch when the session belonged to someone else. */
  const announceLogin = (user: LoginUser) => {
    const previousUser = previousUserRef.current
    if (previousUser && previousUser.id !== user.id) {
      toast.success(t('auth.magicLink.switchedUser', {
        from: displayName(previousUser),
        to: displayName(user),
      }))
      return
    }
    toast.success(t('dashboard.welcome', { name: user.first_name || user.email }))
  }

  useEffect(() => {
    if (!token) {
      setState('error')
      return
    }

    previousUserRef.current = useAuthStore.getState().user

    authApi.verifyMagicLink(token)
      .then((res) => {
        const { user, access_token, refresh_token, companies = [], contracts: rawContracts = [], next_url } = res.data
        const contracts = rawContracts.map(c => ({
          type: c.contract_type as UserContract['type'],
          status: c.status as UserContract['status'],
          expires_at: c.expires_at,
          id: c.id,
          contract_number: c.contract_number,
          roles: c.roles ?? undefined,
        }))
        const isInternal = user.role === 'admin' || user.role === 'sales'
        const nextUrl = safeNext(next_url)

        if (!isInternal && companies.length > 1) {
          setPending({ user, accessToken: access_token, refreshToken: refresh_token, companies, contracts, nextUrl })
          setState('company-select')
          return
        }

        setAuth(user, access_token, refresh_token, companies, contracts)
        announceLogin(user)
        navigate(nextUrl, { replace: true })
        if (isPushSupported() && !isPushPermissionDenied()) {
          setTimeout(() => subscribeToPush(), 2000)
        }
      })
      .catch((err: unknown) => {
        setRenewToken(readRenewToken(err))
        setState('error')
      })
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCompanySelect = (company: UserCompanyRole) => {
    if (!pending) return
    setAuth(pending.user, pending.accessToken, pending.refreshToken, pending.companies, pending.contracts)
    setActiveCompanies([company])
    announceLogin(pending.user)
    navigate(pending.nextUrl, { replace: true })
    if (isPushSupported() && !isPushPermissionDenied()) {
      setTimeout(() => subscribeToPush(), 2000)
    }
  }

  /**
   * STORY-1038: Ein Klick fordert den neuen Anmelde-Link an. Mit Kennzeichen
   * ohne jede Eingabe, sonst ueber die eingetippte Adresse. Eine bestehende
   * Sitzung wird dabei beendet, damit der neue Link sauber greift.
   */
  const handleSendNewLink = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isSending) return
    setIsSending(true)
    try {
      if (useAuthStore.getState().isAuthenticated) logout()
      if (renewToken) {
        await authApi.renewMagicLink(renewToken)
      } else {
        await authApi.requestMagicLink(email.trim())
      }
      setSent(true)
    } catch (err: unknown) {
      if (isConnectionError(err)) {
        toast.error(t('auth.magicLink.networkError'))
      } else {
        // Der Server antwortet immer neutral — auch bei erreichtem Limit oder
        // fehlendem Konto bleibt die Meldung dieselbe.
        setSent(true)
      }
    } finally {
      setIsSending(false)
    }
  }

  if (state === 'loading') {
    return (
      <div className="animate-in text-center">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Loader2 size={32} className="text-primary-600 animate-spin" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('auth.magicLink.verifyingTitle')}
        </h2>
        <p className="text-gray-600">
          {t('auth.magicLink.verifyingSubtitle')}
        </p>
      </div>
    )
  }

  if (state === 'company-select' && pending) {
    return (
      <div className="animate-in">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900">
            {t('auth.login.selectCompany')}
          </h2>
          <p className="text-gray-600 mt-2">
            {t('auth.login.selectCompanySubtitle')}
          </p>
        </div>
        <div className="space-y-3">
          {pending.companies.map((company) => (
            <button
              data-testid="auth-magic-link-company-select-btn"
              key={company.id}
              onClick={() => handleCompanySelect(company)}
              className="w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{company.company_name}</p>
                <p className="text-sm text-gray-500">
                  {company.company_uid}
                  {company.function && ` • ${company.function}`}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          ))}
        </div>
      </div>
    )
  }

  // Error state — STORY-1038: abgelaufener Link, ein Klick sendet einen neuen.
  return (
    <div className="animate-in text-center">
      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <AlertTriangle size={32} className="text-amber-600" aria-hidden="true" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        {t('auth.magicLink.expiredTitle')}
      </h2>
      <p className="text-gray-600 mb-6">
        {t('auth.magicLink.expiredMessage')}
      </p>

      {sent ? (
        /* Neutrale Bestaetigung — nennt die Adresse bewusst nicht. */
        <p
          data-testid="auth-magic-link-sent-message"
          role="status"
          className="mx-auto max-w-sm rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-800"
        >
          {t('auth.magicLink.newLinkSent')}
        </p>
      ) : (
        <form
          data-testid="auth-magic-link-form"
          onSubmit={handleSendNewLink}
          className="mx-auto max-w-sm space-y-4 text-left"
        >
          {/* Rueckfallebene: nur wenn der Token voellig unbekannt ist. */}
          {!renewToken && (
            <div>
              <p className="text-sm text-gray-600 mb-3 text-center">
                {t('auth.magicLink.emailFallbackHint')}
              </p>
              <label htmlFor="magic-link-email" className="label">
                {t('auth.login.email')}
              </label>
              <input
                data-testid="auth-magic-link-email-input"
                id="magic-link-email"
                type="email"
                required
                autoComplete="email"
                placeholder={t('auth.login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
              />
            </div>
          )}
          <button
            data-testid="auth-magic-link-send-btn"
            type="submit"
            disabled={isSending}
            className="btn-primary w-full py-3"
          >
            {isSending ? (
              <Loader2 size={20} className="animate-spin mr-2" aria-hidden="true" />
            ) : (
              <Mail size={20} className="mr-2" aria-hidden="true" />
            )}
            {t('auth.magicLink.sendNewLink')}
          </button>
        </form>
      )}

      {/* Dezente Zeile statt zweiter Schaltflaeche: «Neuen Link senden» bleibt
          der Primaerweg, der Textlink ist nur die Abkuerzung fuer Angemeldete. */}
      {isAuthenticated && (
        <p className="mt-6 text-sm text-gray-500">
          {t('auth.magicLink.stillSignedIn')}{' '}
          <Link
            data-testid="auth-magic-link-continue-link"
            to="/dashboard"
            className="text-primary-600 underline hover:text-primary-700"
          >
            {t('auth.magicLink.continueToApp')}
          </Link>
        </p>
      )}
    </div>
  )
}
